import {
    Component,
    Element,
    Host,
    Listen,
    Prop,
    State,
    forceUpdate,
    h,
} from "@stencil/core";

import {
    ModalForm,
    ModalMessageSuccess,
    ModalMessageError,
} from "./modal/software-updates-modal";
import { SubscriptionInterface, ModalInterface } from "./interface";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-iws-software-updates",
    styleUrl: "software-updates.scss",
})
export class SoftwareUpdates {
    @Element() element: HTMLUxIwsSoftwareUpdatesElement;

    private baseClass = "ux-iws-software-updates";
    @State() subscriptionData: SubscriptionInterface | null = null;

    /** Software Updates subscription status */
    @State() subscriptionStatus: string | null = null;

    /** Endpoint to check for software updates */
    @Prop() url: string;

    /** Endpoint form submission  */
    @Prop() urlFormPostApi: string;

    /** Tooltip link */
    @Prop() tooltipLink: string;

    /** Read more link */
    @Prop() readMoreLink: string;

    /** Privacy policy link */
    @Prop() privacyPolicyLink: string;

    /** optional language for translations (mainly used for storybook) - otherwise language is automatically detected by ux.i18n */
    @Prop() language?: string = "";

    /** Modal form open state */
    @State() modalFormState: boolean = false;

    /** Modal message success open state */
    @State() modalMessageSuccessState: boolean = false;

    /** Modal message error open state */
    @State() modalMessageErrorState: boolean = false;

    /** Check if at least one update type is subscribed */
    @State() updatesSubscribed: boolean = false;

    /** Modal form spinner get state */
    @State() modalFormSpinnerGetState: boolean = false;

    /** Modal form spinner post state */
    @State() modalFormSpinnerPostState: boolean = false;

    /** this is used to rerender the modal each time it was closed */
    @State() modalCloseCount = 0;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    /** Handle modals */
    @Listen("uxModalClose", { target: "window" })
    modalClose(event: CustomEvent) {
        if (event.detail === "software-updates-modal-form") {
            this.modalFormState = false;
            this.modalCloseCount++;
        }
        if (event.detail === "software-updates-modal-message-success") {
            this.modalMessageSuccessState = false;
            this.modalCloseCount++;
        }
        if (event.detail === "software-updates-modal-message-error") {
            this.modalMessageErrorState = false;
            this.modalCloseCount++;
        }
    }

    /** Handle form validation */
    private onFormValidated(event: CustomEvent) {
        if (!event.detail.isValid) {
            return;
        } else {
            event.preventDefault();
            this.handleFormSubmission(event.detail.serializedFormData);
        }
    }

    /** Handle switch input */
    @Listen("uxChange", { target: "window" })
    switchChange(event: CustomEvent) {
        this.checkModalInput();

        if (!event.detail) {
            const checkbox = this.element.querySelector(
                ".ux-iws-software-updates__legal-checkbox"
            ) as HTMLUxCheckboxElement | null;
            if (checkbox !== undefined) {
                checkbox.checked = false;
                checkbox.error = false;
            }
        }
    }

    componentWillLoad() {
        this.fetchData(this.url);
        if (this.language !== "") {
            i18nProvider.changeLanguage(this.language);
        }
    }

    private checkModalInput = () => {
        setTimeout(() => {
            /** Update hidden inputs */

            const updateHiddenInput = (name: string) => {
                const switchInputElement = this.element.querySelector(
                    `ux-modal[name="software-updates-modal-form"] ux-switch-input[data-type="${name}UpdatesSubscribed"]`
                ) as HTMLUxSwitchInputElement | null;
                const hiddenInputElement = this.element.querySelector(
                    `ux-modal[name="software-updates-modal-form"] input[name="${name}UpdatesSubscribed"]`
                ) as HTMLInputElement | null;
                if (
                    switchInputElement !== null &&
                    hiddenInputElement !== null
                ) {
                    hiddenInputElement.value = switchInputElement.checked
                        ? "true"
                        : "false";
                }
            };

            updateHiddenInput("Security");
            updateHiddenInput("Functional");
            updateHiddenInput("Force");

            /** Check if at least one switch input is checked */
            const switches = this.element
                .querySelector('ux-modal[name="software-updates-modal-form"]')
                .querySelectorAll("ux-switch-input");

            const isAnyChecked = Array.from(switches).some(
                (switchInputElement: HTMLUxSwitchInputElement) =>
                    switchInputElement.checked
            );
            this.updatesSubscribed = isAnyChecked;
        }, 0);
    };

    /** Fetch data */
    private fetchData = async (url: string, isModal: boolean = false) => {
        if (isModal) {
            this.checkModalInput();
            this.modalFormState = true;
            this.modalFormSpinnerGetState = true;
        }

        try {
            if (url.includes("mock-data")) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
            const response = await fetch(url);
            const data: SubscriptionInterface = await response.json();

            if (response.ok) {
                this.subscriptionData = data;
                this.subscriptionStatus = data.subscriptionStatus;
                if (isModal) {
                    this.modalFormSpinnerGetState = false;
                }
                return data;
            } else {
                if (isModal) {
                    this.modalMessageErrorState = true;
                    this.modalFormSpinnerGetState = false;
                }
            }
        } catch (error) {
            console.warn(error);
            if (isModal) {
                this.modalMessageErrorState = true;
                this.modalFormSpinnerGetState = false;
            }
        }
    };

    /** Send request and handle response */
    private handleFormSubmission = async (serializedFormData) => {
        this.modalMessageSuccessState = false;
        this.modalMessageErrorState = false;
        this.modalFormSpinnerPostState = true;
        const urlSearchParams = new URLSearchParams(serializedFormData);
        const formData = {};
        for (const [key, value] of urlSearchParams.entries()) {
            formData[key] =
                value === "true" ? true : value === "false" ? false : value;
        }
        try {
            if (this.urlFormPostApi.includes("mock-data")) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
            const options = this.urlFormPostApi.includes("mock-data")
                ? {}
                : {
                      method: "POST",
                      body: JSON.stringify(formData),
                      headers: {
                          "Content-Type": "application/json",
                      },
                  };
            const response = await fetch(this.urlFormPostApi, options);
            if (response.ok) {
                this.modalFormState = false;
                this.modalMessageSuccessState = true;
                this.modalFormSpinnerPostState = false;
                this.fetchData(this.url);
            } else {
                this.modalFormState = false;
                this.modalMessageErrorState = true;
                this.modalFormSpinnerPostState = false;
            }
        } catch {
            this.modalFormState = false;
            this.modalMessageErrorState = true;
            this.modalFormSpinnerPostState = false;
        }
    };

    private renderModalForm = () => {
        const modalOptions: ModalInterface = {
            subscriptionData: this.subscriptionData,
            modalFormState: this.modalFormState,
            modalFormSpinnerGetState: this.modalFormSpinnerGetState,
            modalFormSpinnerPostState: this.modalFormSpinnerPostState,
            updatesSubscribed: this.updatesSubscribed,
            readMoreLink: this.readMoreLink,
            privacyPolicyLink: this.privacyPolicyLink,
            tooltipLink: this.tooltipLink,
            onFormValidated: (e: CustomEvent) => this.onFormValidated(e),
            key: `${this.modalCloseCount}`,
        };
        return ModalForm(modalOptions);
    };

    private renderModalSuccessMessage = () => {
        const modalOptions = {
            modalMessageSuccessState: this.modalMessageSuccessState,
        };
        return ModalMessageSuccess(modalOptions);
    };

    private renderModalErrorMessage = () => {
        const modalOptions = {
            modalMessageErrorState: this.modalMessageErrorState,
        };
        return ModalMessageError(modalOptions);
    };

    private hasSubscribedAllAvailableUpdates = () => {
        const { subscriptionDetails } = this.subscriptionData;
        let subscribed = 0;
        let availableAndNotSubscribed = 0;
        ["Security", "Functional", "Force"].forEach((type: string) => {
            if (subscriptionDetails[`isSubscribedFor${type}Updates`]) {
                subscribed++;
            } else if (
                subscriptionDetails[`isAvailableFor${type}Updates`] &&
                !subscriptionDetails[`isSubscribedFor${type}Updates`]
            ) {
                availableAndNotSubscribed++;
            }
        });
        return availableAndNotSubscribed === 0 && subscribed > 0;
    };

    render() {
        if (this.subscriptionData === null) {
            return null;
        }
        const { t } = i18nProvider;
        const hasSubscribedAllAvailableUpdates =
            this.hasSubscribedAllAvailableUpdates();
        const colClass = !hasSubscribedAllAvailableUpdates
            ? "ux-col-12 ux-col-md-6"
            : "ux-col-12";

        const allowedStatusValues = [
            "UpdatesNotAvailable",
            "UpdatesNotSubscribed",
            "UpdatesPartiallySubscribed",
            "AllUpdatesSubscribed",
            "WaitingForInstallationOfSecurityUpdate",
            "WaitingForInstallationOfFunctionalUpdate",
            "InstallationFailedOfSecurityUpdate",
            "InstallationFailedOfFunctionalUpdate",
        ];
        return (
            this.subscriptionStatus !== null &&
            this.subscriptionStatus !== "UpdatesNotAvailable" &&
            allowedStatusValues.includes(this.subscriptionStatus) && (
                <Host class={this.baseClass} language={this.language}>
                    <div class="ux-row">
                        <div class={colClass}>
                            <span class="ux-info-box__headline ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                {t("softwareUpdates:label")}
                            </span>
                            {hasSubscribedAllAvailableUpdates && (
                                <button
                                    class="ux-icon-button ux-u-ml-xxxs"
                                    onClick={() =>
                                        this.fetchData(this.url, true)
                                    }
                                    data-testid="open-software-updates-modal"
                                >
                                    <ux-icon name="cog"></ux-icon>
                                </button>
                            )}
                            <ux-iws-software-updates-tooltip
                                info={this.subscriptionData}
                                tooltipLink={this.tooltipLink}
                            ></ux-iws-software-updates-tooltip>
                            <ux-w-status
                                type={`SOFTWARE_UPDATES_${this.subscriptionStatus}`}
                            >
                                {t(
                                    `softwareUpdates:subscriptionStatus.${this.subscriptionStatus}.label`
                                )}
                            </ux-w-status>
                        </div>
                        {!hasSubscribedAllAvailableUpdates && (
                            <div class="ux-col-12 ux-col-md-6">
                                <div class="ux-u-mt-xxxs ux-u-mt-md-zero">
                                    <ux-button
                                        class="ux-iws-software-updates__modal-trigger"
                                        icon="cog"
                                        onClick={() =>
                                            this.fetchData(this.url, true)
                                        }
                                        data-testid="open-software-updates-modal"
                                    >
                                        {t("softwareUpdates:subscribeButton")}
                                    </ux-button>
                                </div>
                            </div>
                        )}
                    </div>
                    {this.renderModalForm()}
                    {this.renderModalSuccessMessage()}
                    {this.renderModalErrorMessage()}
                </Host>
            )
        );
    }
}
